import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

// Book purpose options — short labels for mobile
const BOOK_PURPOSES = [
  { id: 'educational', label: 'Learn', icon: 'fa-solid fa-graduation-cap', description: 'Science, history, nature' },
  { id: 'fiction', label: 'Adventure', icon: 'fa-solid fa-dragon', description: 'Fantasy & exciting tales' },
  { id: 'mystery', label: 'Mystery', icon: 'fa-solid fa-magnifying-glass', description: 'Clues, puzzles & secrets' },
  { id: 'moral', label: 'Life Lesson', icon: 'fa-solid fa-lightbulb', description: 'Kindness, bravery, values' },
  { id: 'bedtime', label: 'Bedtime', icon: 'fa-solid fa-moon', description: 'Gentle adventures for winding down' },
  { id: 'fun', label: 'Just Fun', icon: 'fa-solid fa-face-laugh-beam', description: 'Silly & entertaining' },
];

// Fallback languages — only used if supportedLanguages prop is not passed
const DEFAULT_LANGUAGES = [
  { id: 'english', label: 'English', flag: '\u{1F1FA}\u{1F1F8}' },
  { id: 'spanish', label: 'Spanish', flag: '\u{1F1EA}\u{1F1F8}' },
  { id: 'french', label: 'French', flag: '\u{1F1EB}\u{1F1F7}' },
  { id: 'german', label: 'German', flag: '\u{1F1E9}\u{1F1EA}' },
  { id: 'swahili', label: 'Swahili', flag: '\u{1F1F0}\u{1F1EA}' },
  { id: 'italian', label: 'Italian', flag: '\u{1F1EE}\u{1F1F9}' },
  { id: 'portuguese', label: 'Portuguese', flag: '\u{1F1E7}\u{1F1F7}' },
  { id: 'mandarin', label: 'Mandarin', flag: '\u{1F1E8}\u{1F1F3}' },
  { id: 'japanese', label: 'Japanese', flag: '\u{1F1EF}\u{1F1F5}' },
  { id: 'korean', label: 'Korean', flag: '\u{1F1F0}\u{1F1F7}' },
  { id: 'dutch', label: 'Dutch', flag: '\u{1F1F3}\u{1F1F1}' },
  { id: 'russian', label: 'Russian', flag: '\u{1F1F7}\u{1F1FA}' },
  { id: 'arabic', label: 'Arabic', flag: '\u{1F1F8}\u{1F1E6}' },
  { id: 'hindi', label: 'Hindi', flag: '\u{1F1EE}\u{1F1F3}' },
];

const CreateStoryFlow = ({ styles = [], csrfToken, supportedLanguages, myCharacters = [], userName = '' }) => {
  const SUPPORTED_LANGUAGES = supportedLanguages && supportedLanguages.length > 0
    ? supportedLanguages
    : DEFAULT_LANGUAGES;

  const [step, setStep] = useState(1);
  const [bookPurpose, setBookPurpose] = useState(null);
  const [storyLanguage, setStoryLanguage] = useState('english');
  const [languageLearningMode, setLanguageLearningMode] = useState(false);
  const [baseLanguage, setBaseLanguage] = useState('english');
  const [userPrompt, setUserPrompt] = useState('');
  const [vocabWords, setVocabWords] = useState([]);
  const [vocabInput, setVocabInput] = useState('');
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [suggestedTitles, setSuggestedTitles] = useState([]);
  const [storyIdeaId, setStoryIdeaId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [error, setError] = useState(null);

  // AI idea suggestions
  const [suggestedIdeas, setSuggestedIdeas] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [selectedIdeaIndex, setSelectedIdeaIndex] = useState(null);

  const availableCharacters = selectedStyle?.characters || [];
  const effectiveBaseLanguage = languageLearningMode ? baseLanguage : storyLanguage;
  const isBilingual = languageLearningMode && storyLanguage !== baseLanguage;

  const fetchSuggestions = async (purpose, language) => {
    setIdeasLoading(true);
    setSuggestedIdeas([]);
    setSelectedIdeaIndex(null);
    try {
      const response = await axios.post('/adventure_stories/suggest_ideas', {
        book_purpose: purpose || bookPurpose,
        story_language: language || storyLanguage,
        authenticity_token: csrfToken
      });
      setSuggestedIdeas(response.data.ideas || []);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
    setIdeasLoading(false);
  };

  const handleGoToStep2 = () => {
    setStep(2);
    fetchSuggestions(bookPurpose, storyLanguage);
  };

  const handleSelectIdea = (idea, index) => {
    setSelectedIdeaIndex(index);
    setUserPrompt(idea.description);
  };

  const handleGenerateTitles = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        user_prompt: userPrompt,
        style_id: selectedStyle?.id,
        character_id: selectedCharacter?.id,
        book_purpose: bookPurpose,
        story_language: storyLanguage,
        base_language: effectiveBaseLanguage,
        vocab_words: vocabWords.length > 0 ? vocabWords : undefined,
        authenticity_token: csrfToken
      };
      const response = await axios.post('/adventure_stories/create_idea', payload);
      setSuggestedTitles(response.data.titles);
      setStoryIdeaId(response.data.story_idea_id);
      setStep(5);
    } catch (err) {
      console.error('Error generating titles:', err);
      setError(err.response?.data?.error || 'Failed to generate titles. Please try again.');
    }
    setLoading(false);
  };

  const handleSelectTitle = async (title) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post('/adventure_stories/select_title', {
        story_idea_id: storyIdeaId,
        title: title,
        authenticity_token: csrfToken
      });
      if (response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
      }
    } catch (err) {
      console.error('Error selecting title:', err);
      setError(err.response?.data?.error || 'Failed to create story. Please try again.');
      setLoading(false);
    }
  };

  // Compact step indicator — dots on mobile, labels on desktop
  const StepIndicator = () => (
    <div className="flex justify-center items-center mb-6 gap-1 sm:gap-0">
      {[
        { num: 1, label: 'Goal' },
        { num: 2, label: 'Idea' },
        { num: 3, label: 'Style' },
        { num: 4, label: 'Character' },
        { num: 5, label: 'Title' }
      ].map((s) => (
        <div key={s.num} className="flex items-center">
          <div
            className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all
              ${step >= s.num
                ? 'bg-gray-900 text-white'
                : 'border border-gray-200 text-gray-400'}`}
          >
            {step > s.num ? <i className="fa-solid fa-check text-[10px]"></i> : s.num}
          </div>
          <span className={`ml-1 mr-1 sm:ml-2 sm:mr-3 text-xs hidden sm:inline ${step >= s.num ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
            {s.label}
          </span>
          {s.num < 5 && <div className={`w-3 sm:w-6 h-0.5 mx-0.5 sm:mx-1 ${step > s.num ? 'bg-gray-900' : 'bg-gray-200'}`} />}
        </div>
      ))}
    </div>
  );

  const ErrorMessage = () => error && (
    <div className="mb-4 p-3 border border-red-200 rounded-xl text-red-700 text-sm">
      {error}
      <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700 font-bold">&times;</button>
    </div>
  );

  // Spinner component reused in multiple places
  const Spinner = ({ className = 'h-5 w-5' }) => (
    <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  return (
    <div className={`mx-auto px-4 py-4 sm:p-6 ${step === 3 ? 'w-full' : 'max-w-4xl'}`}>
      <StepIndicator />
      <ErrorMessage />

      {/* ==================== STEP 1: Purpose + Language ==================== */}
      {step === 1 && (
        <div className="animate-fadeIn">
          <div className="text-center mb-5">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">What kind of story?</h2>
            <p className="text-gray-400 text-sm">Tap one to pick your goal</p>
          </div>

          {/* Purpose cards — compact 2-col on mobile, 3-col on desktop */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-6">
            {BOOK_PURPOSES.map((purpose) => (
              <div
                key={purpose.id}
                onClick={() => setBookPurpose(purpose.id)}
                className={`p-3 sm:p-4 rounded-xl cursor-pointer transition-all active:scale-95
                  ${bookPurpose === purpose.id
                    ? 'border-2 border-gray-900 bg-white shadow-md'
                    : 'bg-white border border-gray-200 hover:border-gray-900'}`}
              >
                <div className="flex items-center gap-2 sm:block">
                  <div className="w-9 h-9 border border-gray-200 rounded-lg flex items-center justify-center sm:mb-2">
                    <i className={`${purpose.icon} text-gray-900`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm sm:text-base">{purpose.label}</h3>
                    <p className="text-xs text-gray-400 hidden sm:block">{purpose.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Language — collapses neatly */}
          {bookPurpose && (
            <div className="border border-gray-200 rounded-xl p-4 sm:p-5 mb-6 animate-fadeIn">
              <div className="flex items-center gap-3 mb-3">
                <label className="font-bold text-gray-900 text-sm whitespace-nowrap">Story language</label>
                <select
                  value={storyLanguage}
                  onChange={(e) => setStoryLanguage(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 focus:border-gray-900 focus:ring-2 focus:ring-gray-200 text-sm"
                >
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <option key={lang.id} value={lang.id}>{lang.flag} {lang.label}</option>
                  ))}
                </select>
              </div>

              {/* Language learning toggle */}
              <div
                className="flex items-center gap-3 py-2 border-t border-gray-200 cursor-pointer"
                onClick={() => setLanguageLearningMode(!languageLearningMode)}
              >
                <div
                  role="switch"
                  aria-checked={languageLearningMode}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors
                    ${languageLearningMode ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition
                    ${languageLearningMode ? 'translate-x-5' : 'translate-x-0'}`} />
                </div>
                <span className="text-sm text-gray-700">I&apos;m learning a language</span>
              </div>

              {languageLearningMode && (
                <div className="mt-3 p-3 border border-gray-200 rounded-lg animate-fadeIn">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-900 mb-1">I speak...</label>
                      <select
                        value={baseLanguage}
                        onChange={(e) => setBaseLanguage(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm"
                      >
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <option key={lang.id} value={lang.id}>{lang.flag} {lang.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-900 mb-1">I&apos;m learning...</label>
                      <select
                        value={storyLanguage}
                        onChange={(e) => setStoryLanguage(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm"
                      >
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <option key={lang.id} value={lang.id}>{lang.flag} {lang.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {isBilingual && (
                    <p className="mt-2 text-xs text-gray-600">
                      <i className="fa-solid fa-language text-emerald-500 mr-1"></i>
                      Story in {SUPPORTED_LANGUAGES.find(l => l.id === storyLanguage)?.label}, translations in {SUPPORTED_LANGUAGES.find(l => l.id === baseLanguage)?.label}. Tap any sentence while reading to translate!
                    </p>
                  )}
                  {!isBilingual && languageLearningMode && (
                    <p className="mt-2 text-xs text-gray-500">Pick a different language to enable bilingual mode.</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-center">
            <button
              onClick={handleGoToStep2}
              disabled={!bookPurpose}
              className="w-full sm:w-auto px-8 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 active:scale-95 transition-all disabled:opacity-50 disabled:active:scale-100"
            >
              Next: Story Idea <i className="fa-solid fa-arrow-right ml-2"></i>
            </button>
          </div>
        </div>
      )}

      {/* ==================== STEP 2: Story Idea ==================== */}
      {step === 2 && (
        <div className="animate-fadeIn">
          <div className="text-center mb-5">
            <div className="w-12 h-12 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fa-solid fa-wand-magic-sparkles text-xl text-gray-900"></i>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">What&apos;s your story about?</h2>
            <p className="text-gray-400 text-sm">Pick an idea or write your own!</p>
          </div>

          {/* AI Suggestions — loading */}
          {ideasLoading && (
            <div className="text-center py-6 mb-4">
              <Spinner className="h-7 w-7 text-gray-900 mx-auto mb-2" />
              <p className="text-gray-600 font-medium text-sm">Thinking up ideas{userName ? ` for ${userName}` : ''}...</p>
            </div>
          )}

          {/* AI Suggestions — cards */}
          {!ideasLoading && suggestedIdeas.length > 0 && (
            <div className="mb-5">
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3">
                {suggestedIdeas.map((idea, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleSelectIdea(idea, idx)}
                    className={`p-3 rounded-xl cursor-pointer transition-all active:scale-95
                      ${selectedIdeaIndex === idx
                        ? 'border-2 border-gray-900 bg-white shadow-md'
                        : 'bg-white border border-gray-200 hover:border-gray-900'}`}
                  >
                    <h4 className="font-bold text-gray-900 text-xs sm:text-sm leading-tight mb-1">{idea.title}</h4>
                    <p className="text-xs text-gray-500 leading-snug">{idea.description}</p>
                    {selectedIdeaIndex === idx && (
                      <span className="text-gray-900 text-xs font-medium mt-1 block"><i className="fa-solid fa-check"></i></span>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={() => fetchSuggestions()}
                disabled={ideasLoading}
                className="w-full text-center text-xs text-gray-500 hover:text-gray-900 font-medium py-2"
              >
                <i className="fa-solid fa-arrows-rotate mr-1"></i> Show me different ideas
              </button>
            </div>
          )}

          {/* Divider */}
          {!ideasLoading && suggestedIdeas.length > 0 && (
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-xs text-gray-400">Or write your own</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>
          )}

          <textarea
            className="w-full p-3 border-2 border-gray-200 rounded-xl text-base focus:border-gray-900 focus:ring-2 focus:ring-gray-200 transition-all resize-none"
            rows={3}
            placeholder={
              bookPurpose === 'educational'
                ? 'I want to learn about dinosaurs...'
                : bookPurpose === 'moral'
                ? 'A story about being brave...'
                : 'A kid who discovers they can talk to animals...'
            }
            value={userPrompt}
            onChange={(e) => {
              setUserPrompt(e.target.value);
              setSelectedIdeaIndex(null);
            }}
            maxLength={500}
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{userPrompt.length}/500</p>

          {/* Vocab words — only in bilingual mode */}
          {isBilingual && (
            <div className="mt-4 border border-gray-200 rounded-xl p-3 animate-fadeIn">
              <h3 className="font-bold text-gray-900 text-xs mb-1">
                <i className="fa-solid fa-spell-check text-emerald-500 mr-1"></i>
                Words to practice (optional)
              </h3>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={vocabInput}
                  onChange={(e) => setVocabInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && vocabInput.trim() && vocabWords.length < 15) {
                      e.preventDefault();
                      setVocabWords([...vocabWords, vocabInput.trim()]);
                      setVocabInput('');
                    }
                  }}
                  placeholder={`${SUPPORTED_LANGUAGES.find(l => l.id === storyLanguage)?.label || ''} word...`}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  maxLength={50}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (vocabInput.trim() && vocabWords.length < 15) {
                      setVocabWords([...vocabWords, vocabInput.trim()]);
                      setVocabInput('');
                    }
                  }}
                  disabled={!vocabInput.trim() || vocabWords.length >= 15}
                  className="px-3 py-2 bg-gray-900 text-white rounded-lg text-sm disabled:opacity-40"
                >
                  Add
                </button>
              </div>
              {vocabWords.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {vocabWords.map((word, i) => (
                    <span key={i} className="inline-flex items-center border border-gray-200 text-gray-900 text-xs px-2 py-0.5 rounded-full">
                      {word}
                      <button onClick={() => setVocabWords(vocabWords.filter((_, idx) => idx !== i))} className="ml-1 text-gray-400 hover:text-gray-900">&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between items-center mt-5">
            <button onClick={() => setStep(1)} className="px-4 py-3 text-gray-500 hover:text-gray-900 text-sm">
              <i className="fa-solid fa-arrow-left mr-1"></i> Back
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={!userPrompt.trim() || userPrompt.length < 10}
              className="px-6 py-3 bg-gray-900 text-white rounded-xl font-semibold active:scale-95 transition-all disabled:opacity-50"
            >
              Next: Art Style <i className="fa-solid fa-arrow-right ml-2"></i>
            </button>
          </div>
        </div>
      )}

      {/* ==================== STEP 3: Style Selection ==================== */}
      {step === 3 && (
        <div className="animate-fadeIn">
          <div className="text-center mb-5">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Choose your art style</h2>
            <p className="text-gray-400 text-sm">How should your illustrations look?</p>
          </div>

          {/* Full-width 3-col grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {styles.map((style) => (
              <div
                key={style.id}
                onClick={() => {
                  setSelectedStyle(style);
                  setSelectedCharacter(null);
                }}
                className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all active:scale-[0.98]
                  ${selectedStyle?.id === style.id
                    ? 'ring-2 ring-gray-900 ring-offset-2'
                    : 'border border-gray-200 hover:border-gray-900'}`}
              >
                {/* Cover image */}
                {style.cover_image_url ? (
                  <img src={style.cover_image_url} alt={style.title} className="w-full aspect-[4/3] object-cover" />
                ) : (
                  <div className="w-full aspect-[4/3] bg-gray-50 flex items-center justify-center">
                    <i className="fa-solid fa-image text-4xl text-gray-300"></i>
                  </div>
                )}

                {/* Hover overlay — shows title + description */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center px-5 text-center">
                  <p className="text-white font-bold text-xl mb-2">{style.title}</p>
                  <p className="text-white/70 text-sm">{style.subtitle}</p>
                  {style.description && (
                    <p className="text-white/80 text-sm leading-relaxed mt-2">{style.description}</p>
                  )}
                </div>

                {/* Selected badge */}
                {selectedStyle?.id === style.id && (
                  <div className="absolute top-3 right-3 bg-gray-900 text-white px-3 py-1.5 rounded-full text-xs font-medium">
                    <i className="fa-solid fa-check mr-1"></i> Selected
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center">
            <button onClick={() => setStep(2)} className="px-4 py-3 text-gray-500 hover:text-gray-900 text-sm">
              <i className="fa-solid fa-arrow-left mr-1"></i> Back
            </button>
            <button
              onClick={() => setStep(4)}
              disabled={!selectedStyle}
              className="px-6 py-3 bg-gray-900 text-white rounded-xl font-semibold active:scale-95 transition-all disabled:opacity-50"
            >
              Next: Character <i className="fa-solid fa-arrow-right ml-2"></i>
            </button>
          </div>
        </div>
      )}

      {/* ==================== STEP 4: Character Selection ==================== */}
      {step === 4 && (
        <div className="animate-fadeIn">
          <div className="text-center mb-5">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Pick your character</h2>
            <p className="text-gray-400 text-sm">Or skip — the AI will create one</p>
          </div>

          {/* My Characters — horizontal scroll on mobile */}
          {myCharacters.length > 0 && (
            <div className="mb-5">
              <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center">
                <i className="fa-solid fa-star text-amber-500 mr-2"></i>
                My Characters
              </h3>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x sm:grid sm:grid-cols-3 lg:grid-cols-4">
                {myCharacters.map((char) => (
                  <div
                    key={`my-${char.id}`}
                    onClick={() => setSelectedCharacter(selectedCharacter?.id === char.id ? null : char)}
                    className={`flex-shrink-0 w-28 sm:w-auto snap-start p-2 sm:p-3 rounded-xl cursor-pointer transition-all active:scale-95
                      ${selectedCharacter?.id === char.id
                        ? 'border-2 border-gray-900 bg-white shadow-md'
                        : 'bg-white border border-gray-200 hover:border-gray-900'}`}
                  >
                    {char.reference_image_url ? (
                      <img src={char.reference_image_url} alt={char.name} className="w-full aspect-square object-cover rounded-lg mb-1.5" />
                    ) : (
                      <div className="w-full aspect-square bg-gray-50 rounded-lg mb-1.5 flex items-center justify-center">
                        <i className="fa-solid fa-user text-2xl text-gray-300"></i>
                      </div>
                    )}
                    <p className="font-medium text-center text-gray-900 text-xs truncate">{char.name}</p>
                  </div>
                ))}
                <a
                  href="/characters/new"
                  className="flex-shrink-0 w-28 sm:w-auto snap-start p-2 sm:p-3 rounded-xl border-2 border-dashed border-gray-300 hover:border-gray-900 flex flex-col items-center justify-center min-h-[120px]"
                >
                  <i className="fa-solid fa-plus text-xl text-gray-400 mb-1"></i>
                  <p className="text-xs font-medium text-gray-500">New</p>
                </a>
              </div>
            </div>
          )}

          {myCharacters.length === 0 && (
            <div className="mb-5 p-4 border border-gray-200 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="fa-solid fa-palette text-gray-900"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm">Create your own characters!</p>
                  <p className="text-xs text-gray-500">Turn images or ideas into reusable characters.</p>
                </div>
                <a href="/characters/new" className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-medium hover:bg-gray-800 whitespace-nowrap">
                  Create
                </a>
              </div>
            </div>
          )}

          {/* Featured Characters — same horizontal scroll pattern */}
          {availableCharacters.length > 0 && (
            <div className="mb-5">
              <h3 className="text-sm font-bold text-gray-900 mb-2">
                <i className="fa-solid fa-sparkles text-amber-500 mr-2"></i>
                Featured for {selectedStyle?.title}
              </h3>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x sm:grid sm:grid-cols-3 lg:grid-cols-4">
                {availableCharacters.map((char) => (
                  <div
                    key={char.id}
                    onClick={() => setSelectedCharacter(selectedCharacter?.id === char.id ? null : char)}
                    className={`flex-shrink-0 w-28 sm:w-auto snap-start p-2 sm:p-3 rounded-xl cursor-pointer transition-all active:scale-95
                      ${selectedCharacter?.id === char.id
                        ? 'border-2 border-gray-900 bg-white shadow-md'
                        : 'bg-white border border-gray-200 hover:border-gray-900'}`}
                  >
                    {char.reference_image_url ? (
                      <img src={char.reference_image_url} alt={char.name} className="w-full aspect-square object-cover rounded-lg mb-1.5" />
                    ) : (
                      <div className="w-full aspect-square bg-gray-50 rounded-lg mb-1.5 flex items-center justify-center">
                        <i className="fa-solid fa-user text-2xl text-gray-300"></i>
                      </div>
                    )}
                    <p className="font-medium text-center text-gray-900 text-xs truncate">{char.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {availableCharacters.length === 0 && myCharacters.length === 0 && (
            <div className="text-center p-6 border border-gray-200 rounded-xl mb-5">
              <p className="text-gray-500 text-sm">No characters yet — the AI will create one for you!</p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <button onClick={() => setStep(3)} className="px-4 py-3 text-gray-500 hover:text-gray-900 text-sm">
              <i className="fa-solid fa-arrow-left mr-1"></i> Back
            </button>
            <button
              onClick={handleGenerateTitles}
              disabled={loading}
              className="px-6 py-3 bg-gray-900 text-white rounded-xl font-semibold active:scale-95 transition-all disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center">
                  <Spinner className="h-4 w-4 text-white mr-2" />
                  Generating...
                </span>
              ) : (
                <>
                  {selectedCharacter ? 'Generate Titles' : 'Skip & Generate'}
                  <i className="fa-solid fa-arrow-right ml-2"></i>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ==================== STEP 5: Title Selection ==================== */}
      {step === 5 && (
        <div className="animate-fadeIn">
          <div className="text-center mb-5">
            <div className="w-12 h-12 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fa-solid fa-book text-xl text-gray-900"></i>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Pick your title!</h2>
          </div>

          <div className="space-y-2 mb-6">
            {suggestedTitles.map((title, idx) => (
              <button
                key={idx}
                onClick={() => handleSelectTitle(title)}
                disabled={loading}
                className="w-full p-4 text-left bg-white rounded-xl
                  hover:border-gray-900 active:scale-98
                  transition-all text-base font-medium text-gray-900 border border-gray-200
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-gray-900 mr-2 font-bold">{idx + 1}.</span>
                {title}
              </button>
            ))}
          </div>

          <div className="border-t border-gray-200 pt-4">
            <p className="text-gray-400 mb-3 text-center text-xs">Or write your own:</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder="Your custom title..."
                className="flex-1 p-3 border-2 border-gray-200 rounded-xl text-sm focus:border-gray-900 focus:ring-2 focus:ring-gray-200"
                maxLength={100}
              />
              <button
                onClick={() => handleSelectTitle(customTitle)}
                disabled={!customTitle.trim() || loading}
                className="px-4 py-3 bg-gray-900 text-white rounded-xl font-semibold text-sm disabled:opacity-50"
              >
                Go
              </button>
            </div>
          </div>

          <div className="mt-4 text-center">
            <button onClick={() => setStep(4)} disabled={loading} className="text-sm text-gray-500 hover:text-gray-900">
              <i className="fa-solid fa-arrow-left mr-1"></i> Back
            </button>
          </div>

          {loading && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-6 text-center shadow-2xl max-w-xs mx-4 border border-gray-200">
                <i className="fa-solid fa-book-open text-4xl text-gray-900 animate-bounce block mb-3"></i>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Creating your story...</h3>
                <p className="text-gray-500 text-sm">Hang tight while we set everything up!</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

CreateStoryFlow.propTypes = {
  styles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      cover_image_url: PropTypes.string
    })
  ),
  csrfToken: PropTypes.string.isRequired,
  supportedLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      flag: PropTypes.string,
      code: PropTypes.string,
      rtl: PropTypes.bool,
      tts: PropTypes.bool
    })
  ),
  myCharacters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      reference_image_url: PropTypes.string
    })
  ),
  userName: PropTypes.string
};

CreateStoryFlow.defaultProps = {
  styles: [],
  supportedLanguages: null,
  myCharacters: [],
  userName: ''
};

export default CreateStoryFlow;
